import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, Stethoscope, Phone, Mail, FileText, Clock, MapPin, Printer,
  CheckCircle, AlertCircle, Building, Users, ClipboardList
} from 'lucide-react';
import { siteConfig, clinician } from '../data/content';

const ProviderReferralsPage = () => {
  const [formData, setFormData] = useState({
    providerName: '',
    practiceName: '',
    providerPhone: '',
    providerEmail: '',
    patientFirstName: '',
    patientLastName: '',
    patientPhone: '',
    serviceRequested: '',
    urgency: '',
    additionalNotes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: `Dr. ${formData.providerName} (${formData.practiceName})`,
          phone: formData.providerPhone,
          email: formData.providerEmail,
          message: `PROVIDER REFERRAL\n\nPatient: ${formData.patientFirstName} ${formData.patientLastName}\nPatient Phone: ${formData.patientPhone}\nService Requested: ${formData.serviceRequested}\nUrgency: ${formData.urgency}\n\nNotes: ${formData.additionalNotes}`,
          source_page: 'provider-referrals'
        })
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          providerName: '', practiceName: '', providerPhone: '', providerEmail: '',
          patientFirstName: '', patientLastName: '', patientPhone: '',
          serviceRequested: '', urgency: '', additionalNotes: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const services = [
    "Swallowing Evaluation (Clinical Bedside)",
    "Dysphagia Therapy",
    "Speech-Language Evaluation",
    "Speech-Language Therapy",
    "Cognitive-Communication Evaluation",
    "Cognitive-Communication Therapy",
    "Voice Evaluation",
    "Voice Therapy",
    "Other (specify in notes)"
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <section className="pt-32 pb-12 bg-gradient-to-b from-teal-50 to-slate-50">
        <div className="container">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm text-teal-700 hover:text-teal-800 mb-6"
            data-testid="back-to-home"
          >
            <ArrowLeft size={16} />
            Back to Home
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
              <Stethoscope size={24} className="text-teal-700" />
            </div>
            <div>
              <h1 className="heading-1" data-testid="provider-referrals-title">Referring Providers</h1>
              <p className="text-sm text-slate-500">Partner with us for your patients' care</p>
            </div>
          </div>
          <p className="body-large max-w-2xl mt-4">
            Thank you for considering {siteConfig.name} for your patient's speech-language and 
            swallowing therapy needs. We're committed to providing exceptional care and clear 
            communication throughout the treatment process.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="container">
          <div className="grid lg:grid-cols-3 gap-8">
            
            {/* Left Column - Information */}
            <div className="lg:col-span-1 space-y-6">
              
              {/* Services */}
              <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
                <h2 className="heading-3 mb-4 flex items-center gap-2">
                  <ClipboardList size={20} className="text-teal-600" />
                  Services We Provide
                </h2>
                <ul className="space-y-2">
                  {[
                    "Clinical Swallowing Evaluations",
                    "Dysphagia Treatment",
                    "Speech-Language Evaluation & Therapy",
                    "Cognitive-Communication Therapy",
                    "Voice Evaluation & Therapy",
                    "Caregiver Training"
                  ].map((service, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                      <CheckCircle size={16} className="text-teal-600 flex-shrink-0 mt-0.5" />
                      {service}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Service Area */}
              <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
                <h2 className="heading-3 mb-4 flex items-center gap-2">
                  <MapPin size={20} className="text-teal-600" />
                  Service Area
                </h2>
                <p className="text-sm text-slate-600 mb-3">
                  We provide mobile therapy services throughout the Greater Houston area. 
                  Please contact us to confirm service availability in your patient's location.
                </p>
                <p className="text-xs text-slate-500 mt-3">
                  Initial evaluations are conducted in person. Virtual follow-up may be available 
                  for established patients when clinically appropriate.
                </p>
              </div>

              {/* Response Time */}
              <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
                <h2 className="heading-3 mb-4 flex items-center gap-2">
                  <Clock size={20} className="text-teal-600" />
                  Our Commitment
                </h2>
                <ul className="space-y-3 text-sm text-slate-700">
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-teal-600 flex-shrink-0 mt-0.5" />
                    <span>Prompt response to referral inquiries</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-teal-600 flex-shrink-0 mt-0.5" />
                    <span>Timely scheduling of initial evaluations</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-teal-600 flex-shrink-0 mt-0.5" />
                    <span>Clear communication regarding evaluation findings</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-teal-600 flex-shrink-0 mt-0.5" />
                    <span>Direct communication with referring provider</span>
                  </li>
                </ul>
                <p className="text-xs text-slate-500 mt-4 italic">
                  Specific scheduling timelines may vary based on availability and patient needs.
                </p>
              </div>

              {/* Contact */}
              <div className="bg-teal-50 rounded-xl border border-teal-200 p-6">
                <h2 className="heading-3 mb-4 text-teal-900">Direct Contact</h2>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Phone size={18} className="text-teal-700" />
                    <div>
                      <span className="text-xs text-teal-600 block">Office</span>
                      <a href={`tel:${siteConfig.phone}`} className="text-teal-700 font-medium hover:text-teal-800">
                        {siteConfig.phone}
                      </a>
                    </div>
                  </div>
                  {siteConfig.fax && siteConfig.faxVerified && (
                    <div className="flex items-center gap-3">
                      <Printer size={18} className="text-teal-700" />
                      <div>
                        <span className="text-xs text-teal-600 block">Secure Fax</span>
                        <span className="text-teal-700 font-medium">{siteConfig.fax}</span>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <Mail size={18} className="text-teal-700" />
                    <a href={`mailto:${siteConfig.email}`} className="text-teal-700 font-medium hover:text-teal-800">
                      {siteConfig.email}
                    </a>
                  </div>
                </div>
                <p className="text-xs text-teal-700 mt-4">
                  For urgent referrals, please call directly.
                </p>
              </div>
            </div>

            {/* Right Column - Referral Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl border border-slate-200 p-6 md:p-8 shadow-sm">
                <h2 className="heading-2 mb-2">Submit a Referral</h2>
                <p className="body-medium text-slate-600 mb-6">
                  Complete this form to initiate a referral. We'll contact the patient to 
                  schedule their evaluation.
                </p>

                {/* PHI Warning */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex items-start gap-3">
                  <AlertCircle size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-amber-800">
                      <strong>Important:</strong> Please do not include detailed medical history, 
                      diagnoses, medications, or other PHI in this form. Clinical documentation 
                      should be sent through secure channels (fax or secure portal when available).
                    </p>
                  </div>
                </div>

                {submitStatus === 'success' ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                    <CheckCircle size={48} className="text-green-600 mx-auto mb-4" />
                    <h3 className="heading-3 text-green-900 mb-2">Referral Received</h3>
                    <p className="text-green-700">
                      Thank you for your referral. We will contact the patient within 1 business 
                      day to schedule their evaluation. You will receive a confirmation and 
                      follow-up communication.
                    </p>
                    <button
                      onClick={() => setSubmitStatus(null)}
                      className="btn-primary mt-6"
                      data-testid="btn-submit-another"
                    >
                      Submit Another Referral
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Provider Information */}
                    <div>
                      <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                        <Building size={18} className="text-teal-600" />
                        Provider Information
                      </h3>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="providerName" className="form-label">
                            Provider Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            id="providerName"
                            name="providerName"
                            value={formData.providerName}
                            onChange={handleChange}
                            className="form-input"
                            placeholder="Dr. Jane Smith"
                            required
                            data-testid="input-provider-name"
                          />
                        </div>
                        <div>
                          <label htmlFor="practiceName" className="form-label">
                            Practice/Facility Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            id="practiceName"
                            name="practiceName"
                            value={formData.practiceName}
                            onChange={handleChange}
                            className="form-input"
                            placeholder="Houston Medical Center"
                            required
                            data-testid="input-practice-name"
                          />
                        </div>
                        <div>
                          <label htmlFor="providerPhone" className="form-label">
                            Provider Phone <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="tel"
                            id="providerPhone"
                            name="providerPhone"
                            value={formData.providerPhone}
                            onChange={handleChange}
                            className="form-input"
                            placeholder="(713) 555-0100"
                            required
                            data-testid="input-provider-phone"
                          />
                        </div>
                        <div>
                          <label htmlFor="providerEmail" className="form-label">
                            Provider Email <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="email"
                            id="providerEmail"
                            name="providerEmail"
                            value={formData.providerEmail}
                            onChange={handleChange}
                            className="form-input"
                            placeholder="provider@example.com"
                            required
                            data-testid="input-provider-email"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Patient Information */}
                    <div>
                      <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                        <Users size={18} className="text-teal-600" />
                        Patient Information
                      </h3>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="patientFirstName" className="form-label">
                            Patient First Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            id="patientFirstName"
                            name="patientFirstName"
                            value={formData.patientFirstName}
                            onChange={handleChange}
                            className="form-input"
                            required
                            data-testid="input-patient-first-name"
                          />
                        </div>
                        <div>
                          <label htmlFor="patientLastName" className="form-label">
                            Patient Last Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            id="patientLastName"
                            name="patientLastName"
                            value={formData.patientLastName}
                            onChange={handleChange}
                            className="form-input"
                            required
                            data-testid="input-patient-last-name"
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <label htmlFor="patientPhone" className="form-label">
                            Patient/Caregiver Phone <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="tel"
                            id="patientPhone"
                            name="patientPhone"
                            value={formData.patientPhone}
                            onChange={handleChange}
                            className="form-input"
                            placeholder="(713) 555-0100"
                            required
                            data-testid="input-patient-phone"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Service Request */}
                    <div>
                      <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                        <FileText size={18} className="text-teal-600" />
                        Service Request
                      </h3>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="serviceRequested" className="form-label">
                            Service Requested <span className="text-red-500">*</span>
                          </label>
                          <select
                            id="serviceRequested"
                            name="serviceRequested"
                            value={formData.serviceRequested}
                            onChange={handleChange}
                            className="form-input"
                            required
                            data-testid="select-service"
                          >
                            <option value="">Select a service</option>
                            {services.map((service, i) => (
                              <option key={i} value={service}>{service}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label htmlFor="urgency" className="form-label">
                            Urgency <span className="text-red-500">*</span>
                          </label>
                          <select
                            id="urgency"
                            name="urgency"
                            value={formData.urgency}
                            onChange={handleChange}
                            className="form-input"
                            required
                            data-testid="select-urgency"
                          >
                            <option value="">Select urgency</option>
                            <option value="routine">Routine</option>
                            <option value="priority">Priority</option>
                            <option value="urgent">Urgent</option>
                          </select>
                        </div>
                      </div>
                      <div className="mt-4">
                        <label htmlFor="additionalNotes" className="form-label">
                          Additional Notes (optional)
                        </label>
                        <textarea
                          id="additionalNotes"
                          name="additionalNotes"
                          value={formData.additionalNotes}
                          onChange={handleChange}
                          className="form-input form-textarea"
                          rows={3}
                          placeholder="Any additional context (do not include detailed medical information)"
                          data-testid="textarea-notes"
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          Please send clinical documentation through secure channels.
                        </p>
                      </div>
                    </div>

                    {submitStatus === 'error' && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
                        There was an error submitting your referral. Please try again or call us directly.
                      </div>
                    )}

                    <div className="flex flex-col sm:flex-row gap-4 pt-4">
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="btn-accent flex-1 justify-center"
                        data-testid="btn-submit-referral"
                      >
                        {isSubmitting ? 'Submitting...' : 'Submit Referral'}
                      </button>
                      <a
                        href={`tel:${siteConfig.phone}`}
                        className="btn-secondary flex-1 justify-center"
                        data-testid="btn-call-now"
                      >
                        <Phone size={18} />
                        Call Now
                      </a>
                    </div>
                  </form>
                )}
              </div>

              {/* Documentation Note */}
              <div className="mt-6 bg-slate-100 rounded-lg p-4">
                <h3 className="font-semibold text-slate-800 mb-2 text-sm">
                  Sending Clinical Documentation
                </h3>
                <p className="text-xs text-slate-600">
                  For physician orders, medical records, and other clinical documentation, 
                  please use secure methods (fax or secure portal when available). 
                  Contact us for secure transmission options.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProviderReferralsPage;
